import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../context/AuthContext";

function Sidebar({ active }: { active: string }) {
  const { t } = useTranslation();
  const MENU = [
    { labelKey: "account.settings",      path: "/account/settings",      icon: "⚙" },
    { labelKey: "account.reports",       path: "/account/reports",       icon: "📄" },
    { labelKey: "account.accessibility", path: "/account/accessibility", icon: "♿" },
    { labelKey: "account.plans",         path: "/account/plans",         icon: "💳" },
  ];
  return (
    <div style={{ width: "220px", flexShrink: 0, background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "16px 0", height: "fit-content" }}>
      {MENU.map(item => (
        <Link key={item.path} to={item.path}
          style={{ display: "flex", alignItems: "center", gap: "10px", padding: "11px 20px", fontSize: "14px", color: active === item.path ? "#C9A84C" : "var(--text-primary)", fontWeight: active === item.path ? 700 : 400, background: active === item.path ? "rgba(201,168,76,0.08)" : "transparent", borderLeft: active === item.path ? "3px solid #C9A84C" : "3px solid transparent", textDecoration: "none" }}>
          <span>{item.icon}</span> {t(item.labelKey)}
        </Link>
      ))}
      <div style={{ borderTop: "1px solid var(--border)", margin: "8px 0" }} />
      <button onClick={() => { localStorage.removeItem("token"); window.location.href = "/"; }}
        style={{ display: "flex", alignItems: "center", gap: "10px", width: "100%", padding: "11px 20px", fontSize: "14px", color: "var(--error)", background: "transparent", border: "none", cursor: "pointer", fontWeight: 500, borderLeft: "3px solid transparent" }}>
        <span>🚪</span> {t("account.logout")}
      </button>
    </div>
  );
}

const PLANS = [
  {
    id: "free",
    name: "Pay Per Use",
    desc: "Perfect for occasional users",
    monthly: null, yearly: null, perUse: true,
    features: [
      "Capital Gain Report — ₹99/report",
      "Bank Statement to Excel — ₹49/file",
      "No subscription required",
      "Download instantly",
    ],
    cta: "Current Plan",
    highlight: false,
  },
  {
    id: "ca_plan",
    name: "CA Plan",
    desc: "For individual Chartered Accountants",
    monthly: 999, yearly: 7999, perUse: false,
    features: [
      "Unlimited Capital Gain reports",
      "Unlimited Bank Statement conversions",
      "Priority processing",
      "All free tools included",
      "Email support",
    ],
    cta: "Upgrade to CA Plan",
    highlight: true,
  },
  {
    id: "ca_firm",
    name: "CA Firm Plan",
    desc: "For CA firms with multiple clients",
    monthly: 2499, yearly: 19999, perUse: false,
    features: [
      "Everything in CA Plan",
      "Multiple team member logins",
      "White-label reports with firm logo",
      "Client management dashboard",
      "Priority phone support",
    ],
    cta: "Upgrade to Firm Plan",
    highlight: false,
  },
];

export default function Plans() {
  const { user } = useAuth();
  const location = useLocation();
  const [yearly, setYearly] = useState(false);

  const currentPlan = user?.plan || "free";
  const card: React.CSSProperties = { background: "var(--card-bg)", borderRadius: "14px", border: "1.5px solid var(--border)", padding: "24px", marginBottom: "24px" };
  const sectionTitle: React.CSSProperties = { fontSize: "16px", fontWeight: 700, marginBottom: "16px", color: "var(--text-primary)" };

  return (
    <div>
      <div className="page-header-dark">
        <div className="ph-dotgrid" />
        <div className="ph-glow" />
        <div className="ph-inner">
          <div className="ph-breadcrumb">
            <a href="/">Home</a>
            <span>›</span>
            <a href="/account/settings">Account</a>
            <span>›</span>
            <span style={{ color: "#C9A84C" }}>Plans & Subscriptions</span>
          </div>
          <h1>Plans & Subscriptions</h1>
          <p className="ph-subtitle">Manage your subscription and billing</p>
        </div>
      </div>
      <div style={{ maxWidth: "900px", margin: "0 auto", padding: "32px 24px" }}>
      <div style={{ display: "flex", gap: "24px", alignItems: "flex-start" }}>
        <Sidebar active={location.pathname} />
        <div style={{ flex: 1 }}>

          {/* CURRENT PLAN */}
          <div style={card}>
            <h2 style={sectionTitle}>Current Plan</h2>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "16px", background: currentPlan === "free" ? "var(--offwhite)" : "rgba(201,168,76,0.08)", borderRadius: "10px", border: currentPlan === "free" ? "1px solid var(--border)" : "1.5px solid #C9A84C" }}>
              <div>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "6px" }}>
                  <span style={{ fontSize: "18px" }}>{currentPlan === "free" ? "🆓" : "⭐"}</span>
                  <span style={{ fontWeight: 700, fontSize: "16px", color: "var(--text-primary)" }}>
                    {currentPlan === "free" ? "Free Plan" : currentPlan === "ca_plan" ? "CA Plan" : "CA Firm Plan"}
                  </span>
                  {currentPlan !== "free" && (
                    <span style={{ background: "#E2EFDA", color: "#375623", fontSize: "11px", fontWeight: 700, padding: "2px 8px", borderRadius: "6px" }}>● Active</span>
                  )}
                </div>
                <p style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                  {currentPlan === "free"
                    ? "You are on the free plan. Upgrade to access unlimited reports."
                    : "Your subscription is active. Razorpay integration pending for billing details."}
                </p>
              </div>
              {currentPlan === "free" && (
                <Link to="/pricing" style={{ background: "#C9A84C", color: "#2C2C2C", padding: "8px 18px", borderRadius: "8px", fontWeight: 700, fontSize: "13px", textDecoration: "none", whiteSpace: "nowrap", marginLeft: "16px" }}>
                  Upgrade Plan
                </Link>
              )}
            </div>
          </div>

          {/* USAGE STATS */}
          <div style={card}>
            <h2 style={sectionTitle}>Usage This Month</h2>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
              {[
                { label: "Capital Gain Reports", icon: "📈" },
                { label: "Bank Statement Conversions", icon: "🏦" },
              ].map((item, i) => (
                <div key={i} style={{ padding: "14px", background: "var(--offwhite)", borderRadius: "8px", border: "1px solid var(--border)" }}>
                  <div style={{ fontSize: "20px", marginBottom: "6px" }}>{item.icon}</div>
                  <div style={{ fontSize: "13px", color: "var(--text-muted)", marginBottom: "4px" }}>{item.label}</div>
                  <div style={{ fontSize: "12px", color: "#C9A84C", fontWeight: 600 }}>
                    {currentPlan === "free" ? "Pay per use" : "Unlimited"}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* AVAILABLE PLANS */}
          <div style={card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
              <h2 style={{ ...sectionTitle, marginBottom: 0 }}>Available Plans</h2>
              <div style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px" }}>
                <span style={{ color: !yearly ? "var(--text-primary)" : "var(--text-muted)", fontWeight: !yearly ? 600 : 400 }}>Monthly</span>
                <button onClick={() => setYearly(!yearly)} style={{ width: "40px", height: "22px", borderRadius: "11px", background: yearly ? "#C9A84C" : "var(--border)", border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                  <div style={{ width: "16px", height: "16px", borderRadius: "50%", background: "white", position: "absolute", top: "3px", left: yearly ? "21px" : "3px", transition: "left 0.2s" }} />
                </button>
                <span style={{ color: yearly ? "var(--text-primary)" : "var(--text-muted)", fontWeight: yearly ? 600 : 400 }}>
                  Yearly
                  <span style={{ background: "#E2EFDA", color: "#375623", fontSize: "10px", padding: "1px 5px", borderRadius: "4px", marginLeft: "4px", fontWeight: 700 }}>Save 33%</span>
                </span>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "14px" }}>
              {PLANS.map(plan => {
                const isCurrent = (currentPlan === "free" && plan.id === "free") || currentPlan === plan.id;
                return (
                  <div key={plan.id} style={{ border: isCurrent ? "2px solid #52B788" : plan.highlight ? "2px solid #C9A84C" : "1.5px solid var(--border)", borderRadius: "12px", padding: "20px", position: "relative", background: isCurrent ? "rgba(82,183,136,0.05)" : "var(--card-bg)" }}>
                    {isCurrent && (
                      <div style={{ position: "absolute", top: "-10px", left: "50%", transform: "translateX(-50%)", background: "#52B788", color: "white", fontSize: "10px", fontWeight: 700, padding: "2px 10px", borderRadius: "8px", whiteSpace: "nowrap" }}>CURRENT PLAN</div>
                    )}
                    {plan.highlight && !isCurrent && (
                      <div style={{ position: "absolute", top: "-10px", left: "50%", transform: "translateX(-50%)", background: "#C9A84C", color: "#2C2C2C", fontSize: "10px", fontWeight: 700, padding: "2px 10px", borderRadius: "8px", whiteSpace: "nowrap" }}>MOST POPULAR</div>
                    )}
                    <h3 style={{ fontSize: "15px", fontWeight: 700, marginBottom: "4px", color: "var(--text-primary)" }}>{plan.name}</h3>
                    <p style={{ fontSize: "12px", color: "var(--text-muted)", marginBottom: "14px" }}>{plan.desc}</p>

                    {plan.perUse ? (
                      <div style={{ fontSize: "16px", fontWeight: 800, color: "#C9A84C", marginBottom: "14px" }}>Pay as you go</div>
                    ) : (
                      <div style={{ marginBottom: "14px" }}>
                        <span style={{ fontSize: "24px", fontWeight: 800, color: "var(--text-primary)" }}>
                          ₹{yearly ? Math.round((plan.yearly || 0) / 12) : plan.monthly}
                        </span>
                        <span style={{ fontSize: "12px", color: "var(--text-muted)" }}>/month</span>
                        {yearly && <div style={{ fontSize: "11px", color: "#375623", fontWeight: 600, marginTop: "2px" }}>₹{plan.yearly}/year</div>}
                      </div>
                    )}

                    <ul style={{ listStyle: "none", padding: 0, marginBottom: "16px", display: "flex", flexDirection: "column", gap: "6px" }}>
                      {plan.features.map((f, fi) => (
                        <li key={fi} style={{ display: "flex", gap: "6px", fontSize: "12px", color: "var(--text-secondary)" }}>
                          <span style={{ color: "#C9A84C", fontWeight: 700, flexShrink: 0 }}>✓</span>{f}
                        </li>
                      ))}
                    </ul>

                    <button
                      disabled={isCurrent}
                      onClick={() => { if (!isCurrent) window.location.href = "/pricing"; }}
                      style={{ width: "100%", padding: "10px", background: isCurrent ? "#E2EFDA" : plan.highlight ? "#C9A84C" : "transparent", color: isCurrent ? "#375623" : plan.highlight ? "#2C2C2C" : "#C9A84C", border: isCurrent ? "none" : "2px solid #C9A84C", borderRadius: "8px", fontSize: "13px", fontWeight: 700, cursor: isCurrent ? "default" : "pointer" }}
                    >
                      {isCurrent ? "✓ Current Plan" : plan.cta}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>

          {/* PAYMENT HISTORY */}
          <div style={card}>
            <h2 style={sectionTitle}>Payment History & Invoices</h2>
            <div style={{ padding: "32px", background: "var(--offwhite)", borderRadius: "10px", textAlign: "center", border: "1.5px dashed var(--border)" }}>
              <div style={{ fontSize: "36px", marginBottom: "12px" }}>💳</div>
              <p style={{ fontWeight: 600, color: "var(--text-primary)", marginBottom: "6px" }}>Payment history coming soon</p>
              <p style={{ fontSize: "13px", color: "var(--text-muted)" }}>
                Invoices and payment history will appear here once Razorpay payment integration is complete.
              </p>
            </div>
          </div>

        </div>
      </div>
      </div>
    </div>
  );
}
